import { useEffect, useMemo, useState } from "react";
import { X, Clock, ChevronLeft, Sparkles, Search } from "lucide-react";
import { dictionary, Language } from "../../utils/dictionary";
import {
  AdTableRow,
  MetaActionPlan,
  createActionPlan,
  listActionPlans,
} from "../../services/meta/metaService";
import toast from "react-hot-toast";
import Loader from "../common/Loader";
import StatusBadge, { SemaforoStatus } from "./StatusBadge";
import AnalysisLoading from "./AnalysisLoading";
import AnalysisResult from "./AnalysisResult";
import PlatformIcon from "./PlatformIcon";
import CoinFluxi from "../../assets/icons/coin-fluxi.svg";

const ANALYSIS_COST = 10;

interface AdDetailModalProps {
  ad: AdTableRow;
  adAccountId: string;
  dateStart?: string;
  dateEnd?: string;
  language: Language;
  onClose: () => void;
  onCreditsChanged?: () => void;
}

type Tab = "metrics" | "analyses";
type AnalysisView = "list" | "loading" | "result";

const formatCurrency = (n: number): string => {
  if (n === 0) return "$0";
  if (n < 0) return "-$" + Math.abs(n).toFixed(2);
  if (n < 0.01) return "<$0.01";
  return "$" + n.toFixed(2);
};

const formatNumber = (n: number): string => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toLocaleString();
};

export const AdDetailModal = ({
  ad,
  adAccountId,
  dateStart,
  dateEnd,
  language,
  onClose,
  onCreditsChanged,
}: AdDetailModalProps) => {
  const [tab, setTab] = useState<Tab>("metrics");
  const [analyses, setAnalyses] = useState<MetaActionPlan[]>([]);
  const [loadingAnalyses, setLoadingAnalyses] = useState(false);
  const [view, setView] = useState<AnalysisView>("list");
  const [activeAnalysis, setActiveAnalysis] = useState<MetaActionPlan | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadAnalyses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ad.ad_id]);

  const loadAnalyses = async () => {
    setLoadingAnalyses(true);
    try {
      const items = await listActionPlans(ad.ad_id);
      setAnalyses(items);
    } catch {
      // Silent — list empty on error
      setAnalyses([]);
    } finally {
      setLoadingAnalyses(false);
    }
  };

  const handleCreateAnalysis = async () => {
    if (creating) return;
    setCreating(true);
    setView("loading");
    setTab("analyses");
    try {
      const plan = await createActionPlan(ad.ad_id, adAccountId, dateStart, dateEnd);
      setActiveAnalysis(plan);
      setAnalyses((prev) => [plan, ...prev]);
      setView("result");
      onCreditsChanged?.();
      toast.success(dictionary["analysis-created"]?.[language] ?? "Análisis generado");
    } catch (err: any) {
      const trigger = err?.response?.data?.trigger;
      const serverMessage = err?.response?.data?.message;
      let msg: string;
      if (trigger === "INSUFFICIENT_CREDITS") {
        msg = dictionary["analysis-insufficient-credits"]?.[language] ?? "Créditos insuficientes";
      } else if (trigger === "META_ACTION_PLAN_ANALYSIS_FAILED") {
        msg = serverMessage ?? dictionary["analysis-infra-error"]?.[language] ?? "Error al generar análisis";
      } else {
        msg = dictionary["analysis-create-error"]?.[language] ?? "Error al generar análisis";
      }
      toast.error(msg);
      setView("list");
    } finally {
      setCreating(false);
    }
  };

  const handleViewAnalysis = (plan: MetaActionPlan) => {
    setActiveAnalysis(plan);
    setView("result");
  };

  const handleBackToList = () => {
    setView("list");
    setActiveAnalysis(null);
  };

  const funnelLabel = ad.funnel_name ?? "—";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-center items-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div className="flex-1 min-w-0 pr-4">
            <h2 className="text-xl font-bold text-gray-800 truncate">{ad.ad_name}</h2>
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1 flex-wrap">
              <PlatformIcon platform={ad.platform} language={language} />
              {ad.campaign_name && (
                <span>· {dictionary["ad-detail-campaign"]?.[language] ?? "Campaña"}: {ad.campaign_name}</span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              {dictionary["campaigns-col-funnel"]?.[language] ?? "Embudo"}: {funnelLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 pt-4 border-b border-gray-200">
          <button
            type="button"
            onClick={() => setTab("metrics")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === "metrics"
                ? "border-fluxi text-fluxi"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {dictionary["ad-detail-tab-metrics"]?.[language] ?? "Métricas"}
          </button>
          <button
            type="button"
            onClick={() => setTab("analyses")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === "analyses"
                ? "border-fluxi text-fluxi"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {dictionary["ad-detail-tab-analyses"]?.[language] ?? "Análisis"} ({analyses.length})
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === "metrics" && <MetricsTab ad={ad} language={language} />}

          {tab === "analyses" && view === "list" && (
            <AnalysesList
              analyses={analyses}
              loading={loadingAnalyses}
              language={language}
              onView={handleViewAnalysis}
              onCreate={handleCreateAnalysis}
              creating={creating}
            />
          )}

          {tab === "analyses" && view === "loading" && (
            <AnalysisLoading language={language} running={true} />
          )}

          {tab === "analyses" && view === "result" && activeAnalysis && (
            <div>
              <button
                type="button"
                onClick={handleBackToList}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
              >
                <ChevronLeft className="w-4 h-4" />
                {dictionary["common-back"]?.[language] ?? "Volver"}
              </button>
              <AnalysisResult plan={activeAnalysis} language={language} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {dictionary["common-close"]?.[language] ?? "Cerrar"}
          </button>
          <button
            type="button"
            onClick={handleCreateAnalysis}
            disabled={creating}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-fluxi rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-wait"
          >
            <Sparkles className="w-4 h-4" />
            <span>{dictionary["analysis-new"]?.[language] ?? "Nuevo análisis"}</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 text-xs font-semibold">
              {ANALYSIS_COST}
              <img src={CoinFluxi} alt="" className="w-3 h-3" style={{ filter: "brightness(0) invert(1)" }} />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Métricas tab — all 17 rows ---

const MetricsTab = ({ ad, language }: { ad: AdTableRow; language: Language }) => {
  const rows = useMemo(
    () => [
      { label: dictionary["campaigns-col-status"]?.[language] ?? "Estado", value: ad.effective_status === "ACTIVE" ? "Active" : "Paused" },
      { label: dictionary["campaigns-col-ad-name"]?.[language] ?? "Nombre", value: ad.ad_name },
      { label: dictionary["campaigns-col-campaign"]?.[language] ?? "Campaña", value: ad.campaign_name },
      { label: dictionary["campaigns-col-results"]?.[language] ?? "Resultados", value: formatNumber(ad.results) },
      { label: dictionary["campaigns-col-cost-result"]?.[language] ?? "Costo/resultado", value: formatCurrency(ad.cost_per_result) },
      { label: dictionary["campaigns-col-budget"]?.[language] ?? "Presupuesto", value: ad.budget != null ? formatCurrency(ad.budget) : "—" },
      { label: dictionary["campaigns-col-spend"]?.[language] ?? "Gasto", value: formatCurrency(ad.spend) },
      { label: dictionary["campaigns-col-impressions"]?.[language] ?? "Impresiones", value: formatNumber(ad.impressions) },
      { label: dictionary["campaigns-col-reach"]?.[language] ?? "Alcance", value: formatNumber(ad.reach) },
      { label: dictionary["campaigns-col-frequency"]?.[language] ?? "Frecuencia", value: ad.frequency.toFixed(2) },
      { label: dictionary["campaigns-col-cpm"]?.[language] ?? "CPM", value: formatCurrency(ad.cpm) },
      { label: dictionary["campaigns-col-cpc"]?.[language] ?? "CPC", value: formatCurrency(ad.cpc) },
      { label: dictionary["campaigns-col-ctr"]?.[language] ?? "CTR", value: ad.ctr.toFixed(2) + "%" },
      { label: dictionary["campaigns-col-purchases"]?.[language] ?? "Compras", value: formatNumber(ad.purchases) },
      { label: dictionary["campaigns-col-roas"]?.[language] ?? "ROAS", value: ad.roas.toFixed(2) },
      { label: dictionary["campaigns-col-hook-rate"]?.[language] ?? "Hook Rate", value: (ad.hook_rate * 100).toFixed(1) + "%" },
      { label: dictionary["campaigns-col-thruplay"]?.[language] ?? "ThruPlay", value: formatNumber(ad.thruplay) },
    ],
    [ad, language],
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
      {rows.map((row, i) => (
        <div key={i} className="flex items-center justify-between px-4 py-3">
          <span className="text-sm text-gray-500">{row.label}</span>
          <span className="text-sm font-medium text-gray-800 text-right">{row.value}</span>
        </div>
      ))}
    </div>
  );
};

// --- Análisis list sub-view ---

const AnalysesList = ({
  analyses,
  loading,
  language,
  onView,
  onCreate,
  creating,
}: {
  analyses: MetaActionPlan[];
  loading: boolean;
  language: Language;
  onView: (plan: MetaActionPlan) => void;
  onCreate: () => void;
  creating: boolean;
}) => {
  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader />
      </div>
    );
  }

  if (analyses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <Search className="w-7 h-7 text-gray-400" />
        </div>
        <p className="text-sm font-semibold text-gray-800 mb-1">
          {dictionary["analysis-empty-title"]?.[language] ?? "Aún no hay análisis"}
        </p>
        <p className="text-xs text-gray-500 mb-4 text-center max-w-xs">
          {dictionary["analysis-empty-description"]?.[language] ?? "Genera el primer análisis con IA para este anuncio."}
        </p>
        <button
          type="button"
          onClick={onCreate}
          disabled={creating}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-fluxi rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          <Sparkles className="w-4 h-4" />
          <span>{dictionary["analysis-create-first"]?.[language] ?? "Crear primer análisis"}</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 text-xs font-semibold">
            {ANALYSIS_COST}
            <img src={CoinFluxi} alt="" className="w-3 h-3" style={{ filter: "brightness(0) invert(1)" }} />
          </span>
        </button>
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {analyses.map((plan) => {
        const values = Object.values(plan.ai_response.semaforo ?? {});
        const worst: SemaforoStatus = values.includes("red") ? "red" : values.includes("yellow") ? "yellow" : "green";
        return (
          <li
            key={plan.id}
            className="p-3 rounded-lg border border-gray-200 bg-white hover:border-gray-300 transition-colors"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-500">
                    {new Date(plan.created_at).toLocaleString(language === "es" ? "es-ES" : language === "pt" ? "pt-BR" : "en-US", {
                      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                    })}
                  </span>
                  <StatusBadge status={worst} language={language} />
                </div>
                <p className="text-sm text-gray-800 font-medium truncate">
                  {plan.ai_response.analysis_summary.headline}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onView(plan)}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
              >
                {dictionary["common-view"]?.[language] ?? "Ver"}
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default AdDetailModal;
